import { memo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useModal } from '@trinity/chat-components/hooks/useModal';
import { User } from '@trinity/chat-components/components/icons/User.js';
import { getRoleName } from '@trinity/management-app-client/enums';
import { Folder, HelpCircle, Home } from 'react-feather';
import { HorizontalNavLogo } from '@/components/Branding/Logo';
import { InsightsChatIcon } from '@/components/icons/InsightsChat';
import { WorkspaceGlobalSearchBar } from '@/components/WorkspaceGlobalSearchBar';
import { useAppSelector } from '@/store';
import { useHasWorkspacesEnabled, useSelectHomePageLocation } from '@/store/selectors';
import { SupportModal } from '../SupportModal/SupportModal';
import { ApplicationSelector } from './ApplicationSelector';
import { UserBrandDropdown } from './Dropdown';

export const NavBar = memo(() => {
  const workspacesEnabled = useHasWorkspacesEnabled();
  const authenticated = useAppSelector((state) => state.appState.authenticated);
  const username = useAppSelector((state) => state.user.profile.name);
  const photoUrl = useAppSelector((state) => state.user.profile.photoUrl);
  const groupFeatures = useAppSelector((state) => state.features);
  const roles = useAppSelector((state) => state.user.roles);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropDownOpen] = useState(false);

  const [toggleSupportModal, SModal] = useModal(SupportModal);

  const handleDropdownToggle = () => {
    setIsDropDownOpen((prev) => !prev);
  };

  // const homeRoute = useSelectHomePageLocation();

  useEffect(() => {
    if (!isDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropDownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  const showDiagnostics = false;

  return (
    <Root>
      <Wrap>
        <Left>
          {authenticated && <ApplicationSelector />}
          <Link to="/home">
            <HorizontalNavLogo />
          </Link>
          {workspacesEnabled && <WorkspaceGlobalSearchBar />}
        </Left>

        {authenticated && (
          <Right>
            <SModal />
            {groupFeatures.homeButtonTopHeader && (
              <NavLink to="/home">
                <StyledHome />
                <NavLabel>Home</NavLabel>
              </NavLink>
            )}
            {groupFeatures.supportIcon && (
              <NavItem onClick={toggleSupportModal}>
                <StyledSupport />
                <NavLabel>Support</NavLabel>
              </NavItem>
            )}
            {workspacesEnabled && groupFeatures.documentIconVisibility && (
              <NavLink to="/workspaces">
                <StyledFolder />
                <NavLabel>Documents</NavLabel>
              </NavLink>
            )}

            {groupFeatures.chatIconVisibility && (
              <NavLink to="/chat">
                <StyledInsightsChatIcon />
                <NavLabel>Insights Chat</NavLabel>
              </NavLink>
            )}
            <StyledDropdown ref={dropdownRef}>
              <DropdownToggle onClick={handleDropdownToggle}>
                {photoUrl ? <UserAvatar url={photoUrl} /> : <StyledUser />}
                <NavLabel>Account</NavLabel>
              </DropdownToggle>
              <DropdownMenu open={isDropdownOpen}>
                <AccountDropdownItem>
                  {photoUrl ? (
                    <DropdownUserAvatar url={photoUrl} />
                  ) : (
                    <StyledUserIcon />
                  )}
                  <AccountDropdownText>
                    <AccountName>{username}</AccountName>
                    <AccountRole>
                      {roles.map((r) => getRoleName(r)).join(', ')}
                    </AccountRole>
                  </AccountDropdownText>
                </AccountDropdownItem>
                <UserBrandDropdown />
                {/* <DropdownItem to="/admin">
                    <AdminSettingsButton>Admin Settings</AdminSettingsButton>
                  </DropdownItem> */}
                {showDiagnostics && (
                  <DropdownItem to="/diagnostics">
                    <AdminSettingsButton>Diagnostics</AdminSettingsButton>
                  </DropdownItem>
                )}
                <DropdownItem to="/logout">
                  <LogoutButton>Log Out</LogoutButton>
                </DropdownItem>
              </DropdownMenu>
            </StyledDropdown>
          </Right>
        )}
      </Wrap>
    </Root>
  );
});

const Root = styled.div(({ theme }) => ({
  top: 0,
  width: '100%',
  position: 'fixed',
  boxShadow: '0 0 0 0 #0000001a,0 1px 0 0 #0000001a,0 0 0 0 #0000001f',
  backgroundColor: 'white',
  zIndex: 5,
}));

const Wrap = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 40px 0 27px',
  height: 60,
  width: '100%',
});

const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 70px;
`;

const StyledFolder = styled(Folder)`
  color: var(--black-l);
  margin-bottom: 4px;
  width: 30px;
  height: 30px;
`;

const StyledHome = styled(Home)`
  color: var(--black-l);
  margin-bottom: 4px;
  width: 30px;
  height: 30px;
`;

const StyledSupport = styled(HelpCircle)`
  color: var(--black-l);
  margin-bottom: 4px;
  width: 30px;
  height: 30px;
`;

const StyledInsightsChatIcon = styled(InsightsChatIcon)`
  color: var(--black-l);
  margin-bottom: 4px;
  width: 30px;
  height: 30px;
`;

const StyledUser = styled(User)`
  color: var(--black-l);
  margin-bottom: 4px;
  width: 30px;
  height: 30px;
`;

const NavLabel = styled.div`
  color: var(--black-l);
  font-family: var(--font-semibold);
  font-size: 11px;
  text-align: center;
`;

const StyledDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavLink = styled(Link)(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
  ':hover': {
    color: theme.palette.gray.dark1,
  },
}));

const NavItem = styled.div(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
}));

const DropdownToggle = styled.button(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
}));

const DropdownMenu = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  position: absolute;
  top: 60px;
  right: 0;
  z-index: 3;
  min-width: 170px;
  background-color: var(--pri-01);
  background-clip: padding-box;
  box-shadow: 1px 1px 2px 1px rgba(238, 238, 238, 1);
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  padding-bottom: 15px;
  flex-direction: column;
  justify-content: center
  align-items: center;
  border: ${({ theme }) => `1px solid ${theme.palette.gray.light1}`};,
`;

const DropdownItem = styled(Link)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50px',
  boxSizing: 'border-box',
}));

const StyledUserIcon = styled(User)`
  // Replaces StyledUser
  color: var(--black-l);
  width: 35px;
  height: 35px;
  margin-left: 0px;
  font-family: ${({ theme }) => theme.fonts.regular}; /* Added */
`;

const DropdownUserAvatar = styled.div<{ url: string }>`
  width: 35px;
  height: 35px;
  background-image: ${({ url }) => `url(${url})`};
  background-size: cover;
  border-radius: 50%;
`;

const UserAvatar = styled.div<{ url: string }>`
  width: 30px;
  height: 30px;
  background-image: ${({ url }) => `url(${url})`};
  margin-bottom: 4px;
  background-size: cover;
  border-radius: 50%;
`;

const AccountName = styled.div`
  // Replaces Name
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 16px;
  color: black;
  margin-bottom: 8px;
  font-weight: 900;
`;

const AccountRole = styled.div`
  // Replaces Role
  font-family: ${({ theme }) => theme.fonts.regular}; /* Updated */
  font-size: 14px;
  color: var(--gray-d);
`;

const AccountDropdownText = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: theme.fonts.regular,
  color: 'var(--black-l)',
  alignItems: 'flex-start',
  marginLeft: '15px',
}));

const AccountDropdownItem = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '20px 20px',
  width: '250px',
}));

const AdminSettingsButton = styled.div`
  width: 100%;
  text-align: center;
  padding: 10px;
  border: 2px solid
    ${({ theme }) =>
    theme.isTrinityThemed
      ? theme.palette.sentiment.primary.main
      : theme.palette.primary.main};
  border-radius: 4px;
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 14px;
  color: black;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) =>
    theme.isTrinityThemed
      ? theme.palette.sentiment.primary.dark
      : theme.palette.btnPrimary.bg};
  }
`;
const LogoutButton = styled.div`
  width: 225px;
  text-align: center;
  padding: 10px;
  border: none;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 14px;
  color: white;
  background-color: ${({ theme }) =>
    theme.isTrinityThemed
      ? theme.palette.sentiment.primary.main
      : theme.palette.primary.main};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) =>
    theme.isTrinityThemed
      ? theme.palette.sentiment.primary.dark
      : theme.palette.btnSecondary};
{({ theme }) =>
      theme.isTrinityThemed
        ? theme.palette.sentiment.primary.dark
        : theme.palette.btnSecondary};
  }
`;
